import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  ManualInvoice,
  NonCustomerModel,
  SendDownloadInvoice,
} from '../models/invoice/invoice.model';

@Injectable({
  providedIn: 'root',
})
export class InvoiceService {
  private baseRoute: string;
  constructor(private httpClient: HttpClient) {}

  getPaidInvoiceByInvoiceNumber<T>(invoiceId: string): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getpaidinvoicebyinvoicenumber/${invoiceId}`
    );
  }

  getInvoicesByInvoiceNumber<T>(
    invoiceId: string,
    withLinteItems: boolean
  ): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getinvoicebyinvoicenumber/${invoiceId}`
    );
  }

  getRetailInvoiceCustomer<T>(invoiceId: string): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getretailinvoicecustomer/${invoiceId}`
    );
  }

  getManualInvoice<T>(invoiceNumber: string): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getmanualinvoice/${invoiceNumber}`
    );
  }

  getManualInvoiceDetailsByInvoiceNumber<T>(
    invoiceNumber: string
  ): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getmanualinvoicedetailsbyinvoicenumber/${invoiceNumber}`
    );
  }

  getPaidManualInvoiceByInvoiceNumber<T>(invoiceId: string): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getpaidmanualinvoicebyinvoicenumber/${invoiceId}`
    );
  }

  getPartialPaySettingByInvoiceNumber<T>(invoiceNumber: string): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getpartialpaysettingbyinvoicenumber/${invoiceNumber}`
    );
  }

  getSettingsInvoice<T>(): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getsettingsinvoice`
    );
  }

  saveInvoiceEmployee(payload: any) {
    return this.httpClient.post(
      environment.invoiceAPIRoot + '/employee/save',
      payload
    );
  }

  generateManualInvoiceNumber<T>(): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/generatemanualinvoicenumber`
    );
  }

  upsertManualInvoiceAutoSendSchedule(payload: any) {
    return this.httpClient.post(
      environment.invoiceAPIRoot + '/upsertmanualinvoiceautosendschedule',
      payload
    );
  }

  deleteScheduledInvoice(id: number, employeeId: number) {
    return this.httpClient.delete(
      environment.invoiceAPIRoot + `/deletescheduledinvoice/${id}/${employeeId}`
    );
  }
  getAllCustomFieldValuesByInvoiceNumber<T>(
    invoiceNumber: string
  ): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getallcustomfieldvaluesbyinvoicenumber/${invoiceNumber}`
    );
  }
  getCustomerSigByInvoiceNumber<T>(invoiceNumber: string): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getcustomersigbyinvoicenumber/${invoiceNumber}`
    );
  }
  uploadCustomersig(payload: any) {
    return this.httpClient.post(
      environment.invoiceAPIRoot + '/uploadcustomersig',
      payload
    );
  }
  sendInvoice<T>(payload: SendDownloadInvoice): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.invoiceAPIRoot}/send`,
      payload
    );
  }
  deleteManualInvoice<T>(
    invoiceNumber: string,
    updatedBy: number
  ): Observable<T> {
    return this.httpClient.delete<T>(
      `${environment.invoiceAPIRoot}/deletemanualinvoice/${invoiceNumber}/${updatedBy}`
    );
  }
  sendMultipleInvoicesByStatus<T>(
    customerId: number,
    status: string
  ): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.invoiceAPIRoot}/sendmultipleinvoicesbystatus/${customerId}/${status}`,
      null
    );
  }
  getManualInvoicePayments<T>(invoiceNumber: string): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getmanualinvoicepayments/${invoiceNumber}`
    );
  }
  validateInvoiceNumber<T>(invoiceNumber: string): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/${invoiceNumber}/validate`
    );
  }
  // save manual invoice
  saveManualInvoice<T>(payload: ManualInvoice): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.invoiceAPIRoot}/savemanualinvoice`,
      payload
    );
  }
  // Creater Invoice by OrderId
  createInvoice<T>(orderId: string, source: string): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.invoiceAPIRoot}/createinvoicebyorderid/${orderId}/${source}`,
      null
    );
  }
  upsertretailinvoicecustomer<T>(
    invoiceNumber: string,
    customerId: number
  ): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.invoiceAPIRoot}/upsertretailinvoicecustomer/${invoiceNumber}/${customerId}`,
      null
    );
  }

  saveInvoice<T>(
    orderId: string,
    source: string,
    invoiceNumber: string,
    commercialData?: Object
  ): Observable<T> {
    if (invoiceNumber) {
      return this.httpClient.post<T>(
        `${environment.invoiceAPIRoot}/updateinvoicebyorderid/${orderId}/${source}`,
        { commercialData: commercialData }
      );
    } else {
      return this.httpClient.post<T>(
        `${environment.invoiceAPIRoot}/createinvoicebyorderid/${orderId}/${source}`,
        { commercialData: commercialData }
      );
    }
  }

  saveNonCustomerInvoiceDetails<T>(payload: NonCustomerModel): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.invoiceAPIRoot}/createinvoicecustomer`,
      payload
    );
  }

  // Creater Invoice by OrderId
  updateOrderInvoice<T>(orderId: string, source: string): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.invoiceAPIRoot}/updateinvoicebyorderid/${orderId}/${source}`,
      null
    );
  }
}
